/**
 * Sentry initialization for frontend.
 * Only initializes if VITE_SENTRY_DSN is set (disabled in local dev by default).
 */

import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.log("Sentry disabled (no VITE_SENTRY_DSN)");
    return;
  }

  if (initialized) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance tracing
    tracesSampleRate: 0.2,
    // Session replay (disabled by default, enable on error)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
  });

  initialized = true;
  console.log(`Sentry initialized (env: ${import.meta.env.VITE_SENTRY_ENVIRONMENT || "development"})`);
}

/**
 * Set user context for all subsequent events
 */
export function setUser(user: { id: string; email?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * Capture an exception with optional context
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

export { Sentry };
